import React from "react";
import { fmtINR, fmtNum } from "../api";
import Sparkline from "../components/Sparkline";
import AskOIM from "../components/AskOIM";
import { useResource } from "../hooks";
import { api } from "../api";

const KPI_DEFS = [
  { key: "production_pct",    label: "Production",   unit: "%",         spark: "throughput_tph", target: "100", q: "Why is production below nameplate?" },
  { key: "yield_pct",         label: "Yield",        unit: "%",         spark: "yield_pct",      target: "96.0", q: "Why has yield declined?" },
  { key: "throughput_tph",    label: "Throughput",   unit: "t/h",       spark: "throughput_tph", target: "8.33", q: "What is constraining throughput?" },
  { key: "energy_intensity_kwh_per_t", label: "Energy Int.", unit: "kWh/t", spark: "energy_intensity", invert: true, target: "420", q: "How can I cut energy intensity?" },
  { key: "carbon_intensity_tco2_per_t",label: "Carbon Int.", unit: "tCO₂/t",spark: "carbon_intensity", invert: true, target: "2.45", q: "What is driving carbon intensity?" },
  { key: "pcf",               label: "PCF",          unit: "kg/kg",     spark: "pcf",            invert: true, target: "2.45", q: "Why did PCF increase?" },
  { key: "margin_inrh",       label: "Margin / hr",  unit: "₹",         spark: "margin_inrh",    target: "—",   q: "What is squeezing today's margin?", economic: true, money: true },
  { key: "leakage_inrh",      label: "Leakage / hr", unit: "₹",         spark: "leakage_inrh",   invert: true, target: "0", q: "Where is the plant leaking money right now?", economic: true, money: true, amber: true },
];

function formatVal(k, v, money) {
  if (v == null) return "—";
  if (money) return fmtINR(v);
  if (k === "throughput_tph") return fmtNum(v, 2);
  if (k === "pcf") return fmtNum(v, 2);
  if (k === "carbon_intensity_tco2_per_t") return fmtNum(v, 2);
  if (k === "yield_pct" || k === "production_pct") return fmtNum(v, 1);
  if (k === "energy_intensity_kwh_per_t") return fmtNum(v, 0);
  return fmtNum(v, 2);
}

export default function PlantValueCenter({ state }) {
  const opps = useResource(() => api.opportunities(), [], 4000);
  if (!state) return <div className="muted">Loading plant state…</div>;

  const e = state.economics || {};
  const k = state.kpis || {};
  const top = (opps?.opportunities || []).slice(0, 4);

  return (
    <div className="stack" data-testid="plant-value-center">
      <div className="page-title">
        <div>
          <h1>Plant Value Center</h1>
          <div className="sub">Executive view · {state.plant?.name}</div>
        </div>
        <div className="sub">Tick #{state.tick} · {state.mode.toUpperCase()}</div>
      </div>

      {/* KPI ribbon — 8 cells */}
      <div className="kpi-ribbon" data-testid="kpi-ribbon">
        {KPI_DEFS.map(d => (
          <div className="kpi-cell" key={d.key} data-testid={`kpi-${d.key}`}>
            <div className="kpi-label">
              <span>{d.label}</span>
            </div>
            <div className={"kpi-value" + (d.economic ? " economic" : "")}>
              {formatVal(d.key, k[d.key] ?? e[d.key], d.money)}
              {!d.money && <span style={{ fontSize: 10, color: "var(--text-tertiary)", marginLeft: 4 }}>{d.unit}</span>}
            </div>
            <Sparkline kpi={d.spark} invert={d.invert} />
            <div className="kpi-target">Target {d.target}</div>
          </div>
        ))}
      </div>

      {/* Hero card */}
      <div className="hero-card" data-testid="hero-margin-card">
        <div className="hero-grid">
          <div>
            <div className="label">Margin Today (Projected)</div>
            <div className="value lead">{fmtINR(e.margin_today)}</div>
            <div className="metric-sub" style={{ marginTop: 10 }}>
              Revenue {fmtINR(e.revenue_today)} · Resource {fmtINR(e.resource_cost_inrh * 24)} · Utility {fmtINR(e.utility_cost_inrh * 24)}
            </div>
            <AskOIM question="Walk me through today's P&L: where is the margin coming from and where is it leaking?" source="plant_value_center" />
          </div>
          <div>
            <div className="label">Margin Leakage / day</div>
            <div className="value leakage">{fmtINR(e.leakage_today)}</div>
            <div className="metric-sub">Current loss rate</div>
          </div>
          <div>
            <div className="label">Recoverable / year</div>
            <div className="value leakage">{fmtINR(e.recoverable_annual)}</div>
            <div className="metric-sub">If gaps closed</div>
          </div>
          <div>
            <div className="label">Revenue Today</div>
            <div className="value">{fmtINR(e.revenue_today)}</div>
            <div className="metric-sub">{fmtNum(k.production_pct, 1)}% of nameplate</div>
          </div>
        </div>
      </div>

      {/* Bottom split — opportunities + AI summary */}
      <div className="grid-2-1">
        <div className="card" data-testid="top-opportunities-card">
          <div className="card-header">
            <div className="card-title">Top Opportunities</div>
            <span className="chip">Ranked by Annual ₹</span>
          </div>
          <div style={{ padding: "0 0 4px" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Category</th>
                  <th style={{ textAlign: "right" }}>Annual Value</th>
                  <th style={{ textAlign: "right" }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {top.map(o => (
                  <tr key={o.key} data-testid={`opp-row-${o.key}`}>
                    <td>{o.title}</td>
                    <td><span className={`chip ${o.category.toLowerCase()}`}>{o.category}</span></td>
                    <td className="economic" style={{ textAlign: "right" }}>{fmtINR(o.annual_value_inr)}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{Math.round(o.confidence * 100)}%</td>
                  </tr>
                ))}
                {top.length === 0 && (
                  <tr><td colSpan="4" className="muted" style={{ textAlign: "center", padding: 24 }}>No active opportunities</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" data-testid="ai-summary-card">
          <div className="card-header">
            <div className="card-title">AI Summary</div>
            <span className="chip">Live</span>
          </div>
          <div className="card-body">
            {state.anomalies?.length > 0 ? (
              <div>
                <div style={{ color: "var(--brand-amber-soft)", fontSize: 13, fontWeight: 500 }}>
                  {state.anomalies[0].label}
                </div>
                <div style={{ color: "var(--text-secondary)", fontSize: 12.5, lineHeight: 1.55, marginTop: 8 }}>
                  {state.anomalies[0].description}
                </div>
                <div style={{ color: "var(--text-tertiary)", fontSize: 11, fontFamily: "var(--font-mono)", marginTop: 12, letterSpacing: "0.05em" }}>
                  Severity {state.anomalies[0].severity.toFixed(0)}% · Target → {state.anomalies[0].target}
                </div>
              </div>
            ) : (
              <div style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.55 }}>
                All eight twins tracking benchmark. Residual leakage of <strong style={{ color: "var(--brand-amber-soft)" }}>{fmtINR(e.leakage_inrh)}/hr</strong> is within natural variability. No corrective action recommended.
              </div>
            )}
            <AskOIM question="What is today's biggest opportunity at the plant?" source="plant_value_center" label="Discuss with OIM" />
          </div>
        </div>
      </div>
    </div>
  );
}
